import { useEffect, useState, type ReactNode } from "react";
import { FileText, ReceiptText } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { PdfFrame } from "@/components/PdfFrame";
import { EntityCell } from "@/components/PageUi";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

// The shared shell lives in ./page-ui, re-exported so expense screens keep one import.
export {
  TH,
  TableShell,
  HeaderRow,
  rowLink,
  EmptyState,
  LoadingState,
  ErrorState,
  Pager,
  dateRangeQuery,
  DateRangeFilter,
  ScoreMeter,
  type DateRange,
} from "@/components/PageUi";

// Statement descriptors shout ("UBER *TRIP"); mixed-case names came from enrichment and are left as they are.
export function prettyMerchant(name?: string | null) {
  if (!name) return "Unknown merchant";
  if (name !== name.toUpperCase() || !/[A-Z]/.test(name)) return name;
  return name
    .replace(/\*/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => (/\d/.test(word) ? word : word.charAt(0) + word.slice(1).toLowerCase()))
    .join(" ");
}

// "Meals — Fast Food" gets a quieter separator; em-dashes stay out of the UI.
export function prettyCategory(label?: string | null) {
  if (!label) return null;
  return label.replace(/\s+[—–-]\s+/g, " · ");
}

// How a receipt reached us, in words. The enum is the API's vocabulary.
const RECEIPT_SOURCE_LABELS: Record<string, string> = {
  PortalUpload: "Uploaded",
  Api: "API",
  Email: "Email",
  Forwarded: "Forwarded email",
  Mobile: "Mobile",
};

export function receiptSourceLabel(source?: string | null) {
  return source ? (RECEIPT_SOURCE_LABELS[source] ?? source) : null;
}

export function MerchantCell({
  name,
  secondary,
  badges,
}: {
  name?: string | null;
  secondary?: ReactNode;
  badges?: ReactNode;
}) {
  return <EntityCell name={prettyMerchant(name)} secondary={secondary} badges={badges} />;
}

/** Missing stays neutral: most card spend has no receipt and still posts. */
export function ReceiptPresence({ attached }: { attached?: boolean | null }) {
  return attached ? (
    <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
      <ReceiptText className="size-3.5" /> Attached
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <span className="size-3.5 rounded-full border border-dashed border-current" /> Missing
    </span>
  );
}

/** The file URL is short-lived and signed, so it is fetched on mount; PDFs go through PdfFrame to avoid a history entry. */
export function ReceiptPreview({
  receiptId,
  contentType,
  className,
}: {
  receiptId: string;
  contentType?: string | null;
  className?: string;
}) {
  const api = useDolfinClient();
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setFailed(false);
    api.GET("/v1/receipts/{id}/file-url", { params: { path: { id: receiptId } } }).then(({ data }) => {
      if (cancelled) return;
      if (data?.url) setUrl(data.url);
      else setFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [api, receiptId]);

  const frame = cn("overflow-hidden rounded-lg border bg-muted/30", className);

  if (failed) {
    return (
      <div className={cn(frame, "grid place-items-center p-6 text-center text-xs text-muted-foreground")}>
        <span>
          <FileText className="mx-auto mb-2 size-5" />
          The file could not be loaded.
        </span>
      </div>
    );
  }
  if (!url) {
    return (
      <div className={cn(frame, "grid place-items-center")}>
        <Spinner className="size-4 text-muted-foreground" />
      </div>
    );
  }
  if (contentType === "application/pdf") {
    return (
      <div className={frame}>
        <PdfFrame src={url} title="Receipt" className="size-full bg-white" />
      </div>
    );
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className={cn(frame, "block")}>
      <img
        src={url}
        alt="Receipt"
        className="size-full object-contain"
        onError={() => setFailed(true)}
      />
    </a>
  );
}
